import { toast as _sonnerToast } from 'sonner';
import { createToaster } from '@axon/ui';

import { errorHandler, AppError, ErrorType } from './error-handler';

// Notification types
export type NotificationType = 'success' | 'error' | 'warning' | 'info';
export type NotificationPosition =
  | 'top'
  | 'top-right'
  | 'top-left'
  | 'bottom'
  | 'bottom-right'
  | 'bottom-left';

export interface NotificationOptions {
  id?: string;
  title?: string;
  description?: string;
  type?: NotificationType;
  duration?: number; // in milliseconds, null for persistent
  position?: NotificationPosition;
  action?: {
    label: string;
    onClick: () => void;
  };
  closable?: boolean;
  icon?: React.ReactNode;
  onClose?: () => void;
  priority?: 'low' | 'normal' | 'high';
}

export interface AlertBannerOptions {
  id: string;
  title: string;
  description?: string;
  type: NotificationType;
  dismissible?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
  onDismiss?: () => void;
}

export interface ModalNotificationOptions {
  title: string;
  description: string;
  type?: NotificationType;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  showCancel?: boolean;
  closeOnOverlayClick?: boolean;
}

export interface NotificationPreferences {
  enableToasts: boolean;
  enableSounds: boolean;
  enablePushNotifications: boolean;
  enableEmailNotifications: boolean;
  toastPosition: NotificationPosition;
  toastDuration: number;
  doNotDisturb: boolean;
  doNotDisturbStart?: string; // HH:mm format
  doNotDisturbEnd?: string; // HH:mm format
}

// In-app notification
export interface InAppNotification {
  id: string;
  title: string;
  description?: string;
  type: NotificationType;
  timestamp: Date;
  read: boolean;
  category?: string;
  metadata?: Record<string, unknown>;
  actions?: Array<{
    label: string;
    action: string;
    primary?: boolean;
  }>;
}

class NotificationManager {
  private static instance: NotificationManager;
  private toaster = createToaster({
    placement: 'top-end',
    pauseOnPageIdle: true,
  });
  private preferences: NotificationPreferences = {
    enableToasts: true,
    enableSounds: false,
    enablePushNotifications: false,
    enableEmailNotifications: true,
    toastPosition: 'top-right',
    toastDuration: 5000,
    doNotDisturb: false,
  };
  private inAppNotifications: InAppNotification[] = [];
  private alertBanners: Map<string, AlertBannerOptions> = new Map();
  private notificationListeners: ((notification: InAppNotification) => void)[] = [];
  private alertListeners: ((alerts: AlertBannerOptions[]) => void)[] = [];
  private soundEnabled = false;
  private notificationSound?: HTMLAudioElement;

  private constructor() {
    // Initialize notification sound
    if (typeof window !== 'undefined') {
      try {
        this.notificationSound = new Audio('/sounds/notification.mp3');
        this.notificationSound.volume = 0.5;
        // Handle 404 errors gracefully
        this.notificationSound.onerror = () => {
          console.warn('Notification sound not found, disabling audio notifications');
          this.soundEnabled = false;
        };
      } catch (error) {
        console.warn('Failed to initialize notification sound:', error);
        this.soundEnabled = false;
      }
    }

    // Subscribe to error events
    errorHandler.onError(this.handleAppError);

    // Load preferences from localStorage
    this.loadPreferences();
  }

  static getInstance(): NotificationManager {
    if (!NotificationManager.instance) {
      NotificationManager.instance = new NotificationManager();
    }
    return NotificationManager.instance;
  }

  // Load preferences from localStorage
  private loadPreferences() {
    if (typeof window === 'undefined') return;

    const saved = localStorage.getItem('tms_notification_preferences');
    if (saved) {
      try {
        this.preferences = { ...this.preferences, ...JSON.parse(saved) };
      } catch (e) {
        console.error('Failed to load notification preferences:', e);
      }
    }
  }

  // Save preferences to localStorage
  private savePreferences() {
    if (typeof window === 'undefined') return;

    localStorage.setItem('tms_notification_preferences', JSON.stringify(this.preferences));
  }

  // Update preferences
  updatePreferences(preferences: Partial<NotificationPreferences>) {
    this.preferences = { ...this.preferences, ...preferences };
    this.savePreferences();
  }

  getPreferences(): NotificationPreferences {
    return { ...this.preferences };
  }

  // Check if in do not disturb mode
  private isInDoNotDisturbMode(): boolean {
    if (!this.preferences.doNotDisturb) return false;

    const { doNotDisturbStart, doNotDisturbEnd } = this.preferences;
    if (!doNotDisturbStart || !doNotDisturbEnd) return false;

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const [startHour, startMin] = doNotDisturbStart.split(':').map(Number);
    const [endHour, endMin] = doNotDisturbEnd.split(':').map(Number);

    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;

    if (startTime <= endTime) {
      return currentTime >= startTime && currentTime <= endTime;
    } else {
      // Handles cases like 22:00 - 06:00
      return currentTime >= startTime || currentTime <= endTime;
    }
  }

  // Play notification sound
  private playSound() {
    if (
      this.preferences.enableSounds &&
      this.soundEnabled &&
      this.notificationSound &&
      !this.isInDoNotDisturbMode()
    ) {
      this.notificationSound.play().catch((e) => {
        console.warn('Failed to play notification sound:', e);
        this.soundEnabled = false;
      });
    }
  }

  // Handle app errors
  private handleAppError = (error: AppError) => {
    // Don't show notifications for certain error types
    if (error.type === ErrorType.VALIDATION || error.type === ErrorType.NOT_FOUND) {
      return;
    }

    // Show error notification
    this.showToast({
      type: 'error',
      title: 'Hata',
      description: error.message,
      duration: error.type === ErrorType.NETWORK ? undefined : 5000, // Persistent for network errors
    });
  };

  // Show toast notification
  showToast(options: NotificationOptions) {
    if (!this.preferences.enableToasts || this.isInDoNotDisturbMode()) {
      return;
    }

    const {
      id,
      title,
      description,
      type = 'info',
      duration = this.preferences.toastDuration,
      position: _position = this.preferences.toastPosition,
      action: _action,
      closable = true,
      icon: _icon,
      onClose: _onClose,
      priority = 'normal',
    } = options;

    // Play sound for high priority or error notifications
    if (priority === 'high' || type === 'error') {
      this.playSound();
    }

    // Use Chakra UI toaster for better integration
    const toastId = this.toaster.create({
      id,
      title,
      description,
      type: type,
      duration: duration || undefined,
      closable: closable,
    });

    // Also create an in-app notification — but NOT for error toasts.
    // Error toasts are transient operational feedback (network/server/unknown
    // failures funneled through handleAppError). Persisting them would let a
    // single session's failures fill the notification center up to its 100-item
    // cap with "Hata" entries (QA B-250). The notification center is reserved
    // for notifications worth reviewing later, so errors stay toast-only.
    if (type !== 'error') {
      this.addInAppNotification({
        id: toastId,
        title: title || '',
        description,
        type,
        timestamp: new Date(),
        read: false,
      });
    }

    return toastId;
  }

  // Show alert banner
  showAlertBanner(options: AlertBannerOptions) {
    this.alertBanners.set(options.id, options);
    this.notifyAlertListeners();

    // Auto dismiss after 30 seconds if not dismissible
    if (!options.dismissible) {
      setTimeout(() => {
        this.dismissAlertBanner(options.id);
      }, 30000);
    }
  }

  // Dismiss alert banner
  dismissAlertBanner(id: string) {
    const alert = this.alertBanners.get(id);
    if (alert) {
      alert.onDismiss?.();
      this.alertBanners.delete(id);
      this.notifyAlertListeners();
    }
  }

  // Get all alert banners
  getAlertBanners(): AlertBannerOptions[] {
    return Array.from(this.alertBanners.values());
  }

  // Subscribe to alert banner changes
  onAlertChange(listener: (alerts: AlertBannerOptions[]) => void) {
    this.alertListeners.push(listener);
    return () => {
      this.alertListeners = this.alertListeners.filter((l) => l !== listener);
    };
  }

  private notifyAlertListeners() {
    const alerts = this.getAlertBanners();
    this.alertListeners.forEach((listener) => listener(alerts));
  }

  // Show modal notification (returns promise for confirmation)
  async showModal(options: ModalNotificationOptions): Promise<boolean> {
    return new Promise((resolve) => {
      // This will be implemented with a proper modal component
      // For now, use native confirm
      if (typeof window !== 'undefined') {
        const result = window.confirm(`${options.title}\n\n${options.description}`);
        resolve(result);
      } else {
        resolve(false);
      }
    });
  }

  // Add in-app notification
  addInAppNotification(notification: InAppNotification) {
    this.inAppNotifications.unshift(notification);

    // Keep only last 100 notifications
    if (this.inAppNotifications.length > 100) {
      this.inAppNotifications = this.inAppNotifications.slice(0, 100);
    }

    // Notify listeners
    this.notificationListeners.forEach((listener) => listener(notification));

    // Store in localStorage
    this.saveInAppNotifications();
  }

  // Get in-app notifications
  getInAppNotifications(filter?: {
    unreadOnly?: boolean;
    category?: string;
    limit?: number;
  }): InAppNotification[] {
    let notifications = [...this.inAppNotifications];

    if (filter?.unreadOnly) {
      notifications = notifications.filter((n) => !n.read);
    }

    if (filter?.category) {
      notifications = notifications.filter((n) => n.category === filter.category);
    }

    if (filter?.limit) {
      notifications = notifications.slice(0, filter.limit);
    }

    return notifications;
  }

  // Mark notification as read
  markAsRead(notificationId: string) {
    const notification = this.inAppNotifications.find((n) => n.id === notificationId);
    if (notification) {
      notification.read = true;
      this.saveInAppNotifications();
    }
  }

  // Mark all notifications as read
  markAllAsRead() {
    this.inAppNotifications.forEach((n) => (n.read = true));
    this.saveInAppNotifications();
  }

  // Clear all notifications
  clearAll() {
    this.inAppNotifications = [];
    this.saveInAppNotifications();
  }

  // Get unread count
  getUnreadCount(): number {
    return this.inAppNotifications.filter((n) => !n.read).length;
  }

  // Subscribe to notification events
  onNotification(listener: (notification: InAppNotification) => void) {
    this.notificationListeners.push(listener);
    return () => {
      this.notificationListeners = this.notificationListeners.filter((l) => l !== listener);
    };
  }

  // Save in-app notifications to localStorage
  private saveInAppNotifications() {
    if (typeof window === 'undefined') return;

    try {
      localStorage.setItem('tms_in_app_notifications', JSON.stringify(this.inAppNotifications));
    } catch (e) {
      console.error('Failed to save notifications:', e);
    }
  }

  // Load in-app notifications from localStorage
  loadInAppNotifications() {
    if (typeof window === 'undefined') return;

    const saved = localStorage.getItem('tms_in_app_notifications');
    if (saved) {
      try {
        // Drop persisted error entries: profiles polluted before the
        // error-toast exclusion (B-250) carry up to 100 stale "Hata" rows.
        this.inAppNotifications = JSON.parse(saved)
          .filter((n: InAppNotification) => n.type !== 'error')
          .map((n: InAppNotification) => ({
            ...n,
            timestamp: new Date(n.timestamp),
          }));
      } catch (e) {
        console.error('Failed to load notifications:', e);
      }
    }
  }

  // Request push notification permission
  async requestPushPermission(): Promise<boolean> {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }

  // Show push notification
  async showPushNotification(options: {
    title: string;
    body?: string;
    icon?: string;
    badge?: string;
    tag?: string;
    data?: unknown;
  }) {
    if (!this.preferences.enablePushNotifications || this.isInDoNotDisturbMode()) {
      return;
    }

    if (typeof window === 'undefined' || !('Notification' in window)) {
      return;
    }

    const permission = await this.requestPushPermission();
    if (!permission) {
      return;
    }

    const notification = new Notification(options.title, {
      body: options.body,
      icon: options.icon || '/icon-192x192.png',
      badge: options.badge || '/icon-96x96.png',
      tag: options.tag,
      data: options.data,
    });

    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  }
}

// Export singleton instance
export const notificationManager = NotificationManager.getInstance();

// Convenience functions
export function showToast(options: NotificationOptions | string) {
  if (typeof options === 'string') {
    return notificationManager.showToast({ description: options });
  }
  return notificationManager.showToast(options);
}

export function showSuccess(message: string, title?: string) {
  return notificationManager.showToast({
    type: 'success',
    title: title || 'Başarılı',
    description: message,
  });
}

export function showError(message: string, title?: string) {
  return notificationManager.showToast({
    type: 'error',
    title: title || 'Hata',
    description: message,
  });
}

export function showWarning(message: string, title?: string) {
  return notificationManager.showToast({
    type: 'warning',
    title: title || 'Uyarı',
    description: message,
  });
}

export function showInfo(message: string, title?: string) {
  return notificationManager.showToast({
    type: 'info',
    title: title || 'Bilgi',
    description: message,
  });
}

export function confirm(options: ModalNotificationOptions | string): Promise<boolean> {
  if (typeof options === 'string') {
    return notificationManager.showModal({
      title: 'Onay',
      description: options,
    });
  }
  return notificationManager.showModal(options);
}
